import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { apiService, Transaction, AnalyticsResponse, Account, TypeCounts, EXPENSE_CATEGORIES, INCOME_CATEGORIES } from '../services/api';
import { usePrivacy } from '../contexts/PrivacyContext';
import { TransactionStats } from '../components/TransactionStats';
import { Button } from '../components/ui/button';
import { Modal } from '../components/ui/modal';
import { 
  Landmark, 
  XCircle, 
  BanknoteArrowDown, 
  BanknoteArrowUp, 
  Sparkles, 
  Store, 
  ChevronLeft, 
  ChevronRight, 
  ChevronDown, 
  ArrowLeftRight,
  Filter,
  Search,
  X,
  Calendar,
  Tag,
  Building2,
  Check
} from 'lucide-react';

// Simple Tooltip component using Portal
const Tooltip: React.FC<{ 
  children: React.ReactNode; 
  content: string | null | undefined;
  label?: string;
}> = ({ children, content, label }) => {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (!content) return;
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      });
    }
    setShow(true);
  };

  const handleMouseLeave = () => {
    setShow(false);
  };

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {show && content && createPortal(
        <div
          style={{
            position: 'absolute',
            top: position.top,
            left: position.left,
            zIndex: 9999,
            backgroundColor: 'var(--theme-text)',
            color: 'var(--theme-background)',
            padding: '8px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 500,
            maxWidth: '300px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            pointerEvents: 'none',
          }}
        >
          {label && (
            <div style={{ opacity: 0.7, fontSize: '10px', marginBottom: '2px' }}>{label}</div>
          )}
          <div>{content}</div>
        </div>,
        document.body
      )}
    </>
  );
};

interface EditingField {
  transactionId: string;
  field: 'merchant_name' | 'category' | 'transaction_date';
  value: string;
}

export const TransactionsPage: React.FC = () => {
  const { isPrivacyMode } = usePrivacy();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [typeCounts, setTypeCounts] = useState<TypeCounts>({ all: 0, expense: 0, income: 0, transfer: 0, uncategorized: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<EditingField | null>(null);
  const editingFieldRef = useRef<EditingField | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [quickFilterCategory, setQuickFilterCategory] = useState<string | null>(null);
  const [quickFilterType, setQuickFilterType] = useState<'income' | 'expense' | 'transfer' | null>(null);
  const [quickFilterMonth, setQuickFilterMonth] = useState<string | null>(null);
  const [quickFilterAccount, setQuickFilterAccount] = useState<string | null>(null);
  const [urlFiltersApplied, setUrlFiltersApplied] = useState(false);
  const [hoveredRecommendation, setHoveredRecommendation] = useState<string | null>(null);
  const [hoveredAccount, setHoveredAccount] = useState<string | null>(null);
  const [logoErrors, setLogoErrors] = useState<Set<string>>(new Set());
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [sortOrder] = useState<'desc' | 'asc'>('desc');
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 50;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Focus search input when opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Generate available months from analytics data
  const availableMonths = useMemo(() => {
    if (!analytics?.monthly_spend) return [];
    return analytics.monthly_spend.map(m => m.month);
  }, [analytics?.monthly_spend]);

  // Count active filters (excluding search)
  const activeFilterCount = [
    quickFilterCategory,
    quickFilterAccount,
    quickFilterMonth,
  ].filter(Boolean).length;

  const fetchTransactions = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      
      let start_date: string | undefined;
      let end_date: string | undefined;
      
      if (quickFilterMonth) {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const [monthName, year] = quickFilterMonth.split(' ');
        const monthIndex = monthNames.indexOf(monthName);
        
        if (monthIndex !== -1) {
          const startDate = new Date(parseInt(year), monthIndex, 1);
          const endDate = new Date(parseInt(year), monthIndex + 1, 0);
          start_date = startDate.toISOString().split('T')[0];
          end_date = endDate.toISOString().split('T')[0];
        }
      }
      
      const response = await apiService.getTransactions({
        search: debouncedSearch || undefined,
        category: quickFilterCategory || undefined,
        transaction_type: quickFilterType || undefined,
        start_date,
        end_date,
        sort_by: 'date',
        sort_order: sortOrder,
        page: currentPage,
        page_size: pageSize,
        account_id: quickFilterAccount || undefined,
        include_counts: true,
        signal,
      });
      
      // Only update state if not aborted
      if (!signal?.aborted) {
        setTransactions(response.transactions);
        setTotalCount(response.total);
        
        if (response.type_counts) {
          setTypeCounts(response.type_counts);
        }
      }
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      if (!signal?.aborted) {
        setError(err instanceof Error ? err.message : 'Failed to load transactions');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [currentPage, quickFilterCategory, quickFilterType, quickFilterMonth, quickFilterAccount, sortOrder, debouncedSearch]);

  const handleCategoryQuickFilter = (category: string | null) => {
    if (category === null) {
      setQuickFilterCategory(null);
    } else {
      if (quickFilterCategory === category) {
        setQuickFilterCategory(null);
      } else {
        setQuickFilterCategory(category);
      }
    }
    setCurrentPage(1);
  };
  
  const handleTypeQuickFilter = (type: 'income' | 'expense' | 'transfer' | null) => {
    if (type === null) {
      setQuickFilterType(null);
      setQuickFilterCategory(null);
    } else {
      if (quickFilterType === type) {
        setQuickFilterType(null);
        setQuickFilterCategory(null);
      } else {
        setQuickFilterType(type);
        setQuickFilterCategory(null);
      }
    }
    setCurrentPage(1);
  };

  const handleMonthQuickFilter = (month: string) => {
    if (quickFilterMonth === month) {
      setQuickFilterMonth(null);
    } else {
      setQuickFilterMonth(month);
    }
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    setQuickFilterCategory(null);
    setQuickFilterType(null);
    setQuickFilterMonth(null);
    setQuickFilterAccount(null);
    setSearchQuery('');
    setDebouncedSearch('');
    setIsSearchOpen(false);
    setCurrentPage(1);
  };

  const handleAccountFilter = (accountId: string | null) => {
    setQuickFilterAccount(accountId);
    setCurrentPage(1);
  };

  const hasActiveFilters = quickFilterCategory !== null || quickFilterType !== null || quickFilterMonth !== null || quickFilterAccount !== null || debouncedSearch !== '';

  const fetchAccounts = async (signal?: AbortSignal) => {
    try {
      const response = await apiService.getAccounts(signal);
      if (!signal?.aborted) {
        setAccounts(response.accounts);
      }
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Failed to load accounts:', err);
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchTransactions(abortController.signal);
    return () => abortController.abort();
  }, [fetchTransactions]);
  
  useEffect(() => {
    const abortController = new AbortController();
    fetchAnalytics(quickFilterAccount, abortController.signal);
    return () => abortController.abort();
  }, [quickFilterAccount]);
  
  useEffect(() => {
    const abortController = new AbortController();
    fetchAccounts(abortController.signal);
    return () => abortController.abort();
  }, []);
  
  useEffect(() => {
    if (!urlFiltersApplied && !loading) {
      const category = searchParams.get('category');
      const type = searchParams.get('type') || searchParams.get('transaction_type');
      const startDate = searchParams.get('start_date');
      const endDate = searchParams.get('end_date');
      const accountId = searchParams.get('account_id');
      
      let hasFilters = false;
      
      if (category) {
        setQuickFilterCategory(category);
        hasFilters = true;
      }
      
      if (type && (type === 'income' || type === 'expense' || type === 'transfer')) {
        setQuickFilterType(type as 'income' | 'expense' | 'transfer');
        hasFilters = true;
      }
      
      if (accountId) {
        setQuickFilterAccount(accountId);
        hasFilters = true;
      }
      
      if (startDate && endDate) {
        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);
        
        if (startDateObj.getMonth() === endDateObj.getMonth() && 
            startDateObj.getFullYear() === endDateObj.getFullYear()) {
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const monthLabel = `${monthNames[startDateObj.getMonth()]} ${startDateObj.getFullYear()}`;
          setQuickFilterMonth(monthLabel);
          hasFilters = true;
        }
      }
      
      if (hasFilters) {
        setSearchParams({});
        setUrlFiltersApplied(true);
        setCurrentPage(1);
      }
    }
  }, [loading, searchParams, setSearchParams, urlFiltersApplied]);

  const fetchAnalytics = async (accountId?: string | null, signal?: AbortSignal) => {
    try {
      setAnalyticsLoading(true);
      
      if (accountId) {
        const response = await apiService.getTransactions({
          account_id: accountId,
          page_size: 10000,
          include_recommendations: false,
          signal,
        });
        
        if (signal?.aborted) return;
        
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        
        const currentMonthTransactions = response.transactions.filter(t => {
          const txnDate = new Date(t.transaction_date);
          return txnDate.getMonth() === currentMonth && txnDate.getFullYear() === currentYear;
        });
        
        const totalSpend = currentMonthTransactions
          .filter(t => t.transaction_type === 'expense')
          .reduce((sum, t) => sum + Math.abs(parseFloat(t.amount)), 0);
        
        setAnalytics({
          current_month: {
            total_transactions: currentMonthTransactions.length,
            total_spend: totalSpend.toString()
          },
          spend_by_category: [],
          income_by_category: [],
          monthly_spend: [],
          monthly_expense_by_category: [],
          monthly_income_by_category: []
        });
      } else {
        const response = await apiService.getAnalytics(undefined, undefined, signal);
        if (!signal?.aborted) {
          setAnalytics(response);
        }
      }
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Failed to load analytics:', err);
    } finally {
      if (!signal?.aborted) {
        setAnalyticsLoading(false);
      }
    }
  };

  const handleFieldClick = (transaction: Transaction, field: EditingField['field']) => {
    let value: string;
    if (field === 'transaction_date') {
      value = transaction.transaction_date;
    } else if (field === 'merchant_name') {
      value = transaction.merchant_name;
    } else if (field === 'category') {
      value = transaction.category;
    } else {
      value = '';
    }
    
    const fieldData = { transactionId: transaction.id, field, value };
    setEditingField(fieldData);
    editingFieldRef.current = fieldData;
  };

  const handleFieldChange = (value: string) => {
    if (!editingField) return;
    const updated = { ...editingField, value };
    setEditingField(updated);
    editingFieldRef.current = updated;
  };

  // For date fields, just update the value (save happens on blur)
  const handleDateChange = (value: string) => {
    if (!editingField) return;
    const updated = { ...editingField, value };
    setEditingField(updated);
    editingFieldRef.current = updated;
  };

  const saveField = async (fieldToSave: EditingField) => {
    const transaction = transactions.find(t => t.id === fieldToSave.transactionId);
    if (!transaction) return;
    
    let originalValue: string;
    if (fieldToSave.field === 'merchant_name') {
      originalValue = transaction.merchant_name;
    } else if (fieldToSave.field === 'transaction_date') {
      originalValue = transaction.transaction_date;
    } else {
      originalValue = transaction.category;
    }
    
    if (originalValue === fieldToSave.value) {
      setEditingField(null);
      editingFieldRef.current = null;
      return;
    }
    
    try {
      setSavingId(fieldToSave.transactionId);
      const updateData: Record<string, string> = {
        [fieldToSave.field]: fieldToSave.value
      };
      
      await apiService.updateTransaction(fieldToSave.transactionId, updateData);
      
      setTransactions(prevTransactions => 
        prevTransactions.map(t => {
          if (t.id === fieldToSave.transactionId) {
            return { ...t, [fieldToSave.field]: fieldToSave.value };
          }
          return t;
        })
      );
      
      setEditingField(null);
      editingFieldRef.current = null;
      fetchAnalytics(quickFilterAccount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update transaction');
      setEditingField(null);
      editingFieldRef.current = null;
    } finally {
      setSavingId(null);
    }
  };

  const handleFieldBlur = async () => {
    if (!editingField) return;
    
    // For date fields, delay slightly to allow onChange to complete first
    if (editingField.field === 'transaction_date') {
      setTimeout(() => {
        const latestField = editingFieldRef.current;
        if (latestField) {
          saveField(latestField);
        }
      }, 100);
    } else {
      await saveField(editingField);
    }
  };

  const handleToggleTransfer = async (transaction: Transaction) => {
    try {
      setSavingId(transaction.id);
      
      const newType: 'income' | 'expense' | 'transfer' = transaction.transaction_type === 'transfer' 
        ? (parseFloat(transaction.amount) >= 0 ? 'income' : 'expense')
        : 'transfer';
      
      const oldType = transaction.transaction_type;
      
      await apiService.updateTransaction(transaction.id, { transaction_type: newType });
      
      setTransactions(prevTransactions => 
        prevTransactions.map(t => {
          if (t.id === transaction.id) {
            return { ...t, transaction_type: newType };
          }
          return t;
        })
      );
      
      setTypeCounts(prev => {
        const updated = { ...prev };
        if (oldType === 'transfer') updated.transfer--;
        else if (oldType === 'income') updated.income--;
        else updated.expense--;
        if (newType === 'transfer') updated.transfer++;
        else if (newType === 'income') updated.income++;
        else updated.expense++;
        return updated;
      });
      
      fetchAnalytics(quickFilterAccount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update transaction');
    } finally {
      setSavingId(null);
    }
  };

  const formatAmount = (amount: string) => {
    if (isPrivacyMode) return '$••••';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(parseFloat(amount)));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateFull = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getAccount = (accountId: string) => {
    return accounts.find(a => a.id === accountId);
  };

  const renderTransactionItem = (transaction: Transaction, index: number) => {
    const account = getAccount(transaction.account_id);
    const isIncome = transaction.transaction_type === 'income';
    const isTransfer = transaction.transaction_type === 'transfer';
    const isSaving = savingId === transaction.id;
    const hasLogo = transaction.merchant_logo_url && !logoErrors.has(transaction.id);
    const recommendation = transaction.recommendation;
    const hasRecommendation = !!recommendation;
    const isHoveredRecommendation = hoveredRecommendation === transaction.id;
    const isLast = index === transactions.length - 1;

    const isEditingMerchant = editingField?.transactionId === transaction.id && editingField?.field === 'merchant_name';
    const isEditingDate = editingField?.transactionId === transaction.id && editingField?.field === 'transaction_date';
    const isEditingCategory = editingField?.transactionId === transaction.id && editingField?.field === 'category';

    const categories = isIncome ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

    return (
      <div 
        key={transaction.id}
        className={`transition-all duration-200 ${isSaving ? 'opacity-50' : ''}`}
        style={{ 
          borderBottom: !isLast 
            ? '1px solid color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)' 
            : 'none'
        }}
      >
        {/* Mobile Layout */}
        <div className="md:hidden p-4">
          <div className="flex items-start gap-3">
            {/* Merchant Logo */}
            <div
              className="relative flex-shrink-0"
              title={transaction.original_description || transaction.merchant_name}
            >
              {hasLogo ? (
                <img 
                  src={transaction.merchant_logo_url || undefined} 
                  alt={`${transaction.merchant_name} logo`}
                  className="h-11 w-11 rounded-xl object-cover"
                  onError={() => setLogoErrors(prev => new Set(prev).add(transaction.id))}
                />
              ) : (
                <div 
                  className="h-11 w-11 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)' }}
                >
                  {isTransfer ? (
                    <ArrowLeftRight className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
                  ) : isIncome ? (
                    <BanknoteArrowUp className="h-5 w-5" style={{ color: 'var(--theme-income)' }} />
                  ) : (
                    <BanknoteArrowDown className="h-5 w-5" style={{ color: 'var(--theme-expense)' }} />
                  )}
                </div>
              )}
              {/* Type indicator dot */}
              <div 
                className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center"
                style={{ 
                  backgroundColor: isTransfer 
                    ? 'var(--theme-accent)' 
                    : isIncome 
                      ? 'var(--theme-income)' 
                      : 'var(--theme-expense)',
                  borderColor: 'var(--theme-surface)'
                }}
              >
                {isTransfer ? (
                  <ArrowLeftRight className="h-2 w-2 text-white" />
                ) : isIncome ? (
                  <BanknoteArrowUp className="h-2 w-2 text-white" />
                ) : (
                  <BanknoteArrowDown className="h-2 w-2 text-white" />
                )}
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  {isEditingMerchant ? (
                    <input
                      type="text"
                      value={editingField.value as string}
                      onChange={(e) => handleFieldChange(e.target.value)}
                      onBlur={handleFieldBlur}
                      autoFocus
                      className="px-2 py-1 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 w-full"
                      style={{ 
                        backgroundColor: 'var(--theme-surface)',
                        borderColor: 'var(--theme-primary)',
                        color: 'var(--theme-text)'
                      }}
                    />
                  ) : (
                    <p 
                      className="font-semibold text-sm truncate cursor-pointer"
                      style={{ color: 'var(--theme-text)' }}
                      onClick={() => handleFieldClick(transaction, 'merchant_name')}
                    >
                      {transaction.merchant_name}
                    </p>
                  )}
                  
                  <div className="flex items-center gap-1.5 mt-1">
                    {isEditingDate ? (
                      <input
                        type="date"
                        value={editingField.value as string}
                        onChange={(e) => handleDateChange(e.target.value)}
                        onBlur={handleFieldBlur}
                        autoFocus
                        className="px-2 py-1 text-xs rounded-lg"
                        style={{ 
                          backgroundColor: 'var(--theme-surface)',
                          color: 'var(--theme-text)'
                        }}
                      />
                    ) : (
                      <span 
                        className="text-xs cursor-pointer"
                        style={{ color: 'var(--theme-text-secondary)' }}
                        onClick={() => handleFieldClick(transaction, 'transaction_date')}
                      >
                        {formatDate(transaction.transaction_date)}
                      </span>
                    )}
                    
                    {!isTransfer && (
                      <>
                        <span style={{ color: 'var(--theme-text-secondary)', opacity: 0.5 }}>•</span>
                        {isEditingCategory ? (
                          <select
                            value={editingField.value as string}
                            onChange={(e) => handleFieldChange(e.target.value)}
                            onBlur={handleFieldBlur}
                            autoFocus
                            className="text-xs rounded-lg px-2 py-1"
                            style={{ 
                              backgroundColor: 'var(--theme-surface)',
                              color: 'var(--theme-text)'
                            }}
                          >
                            {categories.map((category) => (
                              <option key={category} value={category}>{category}</option>
                            ))}
                          </select>
                        ) : (
                          <button
                            className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-lg transition-colors"
                            style={{ 
                              backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)',
                              color: 'var(--theme-text-secondary)'
                            }}
                            onClick={() => handleFieldClick(transaction, 'category')}
                          >
                            {hasRecommendation && (
                              <Sparkles className="h-3 w-3" style={{ color: 'var(--theme-primary)' }} />
                            )}
                            <span className="truncate max-w-[100px]">{transaction.category}</span>
                            <ChevronDown className="h-3 w-3 opacity-50" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                
                {/* Amount & Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleToggleTransfer(transaction)}
                    className="p-1.5 rounded-lg transition-colors"
                    style={{ 
                      backgroundColor: isTransfer 
                        ? 'color-mix(in srgb, var(--theme-accent) 15%, transparent)' 
                        : 'transparent',
                      color: isTransfer ? 'var(--theme-accent)' : 'var(--theme-text-secondary)'
                    }}
                    title={isTransfer ? 'Remove transfer label' : 'Mark as transfer'}
                  >
                    <ArrowLeftRight className="h-4 w-4" />
                  </button>
                  
                  <span 
                    className="font-bold text-sm"
                    style={{ 
                      color: isTransfer 
                        ? 'var(--theme-accent)' 
                        : isIncome 
                          ? 'var(--theme-income)' 
                          : 'var(--theme-text)' 
                    }}
                  >
                    {isTransfer ? '' : (isIncome ? '+' : '-')}{formatAmount(transaction.amount)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:flex items-center gap-4 px-5 py-4">
          {/* Bank Icon with Tooltip */}
          <div 
            className="relative flex-shrink-0"
            onMouseEnter={() => setHoveredAccount(transaction.id)}
            onMouseLeave={() => setHoveredAccount(null)}
          >
            <Landmark className="h-4 w-4" style={{ color: 'var(--theme-text-secondary)' }} />
            {hoveredAccount === transaction.id && account && (
              <div 
                className="absolute z-20 top-full left-0 mt-2 px-3 py-2 text-xs font-medium rounded-xl shadow-lg whitespace-nowrap"
                style={{ 
                  backgroundColor: 'var(--theme-text)',
                  color: 'var(--theme-background)'
                }}
              >
                <div>{account.name}</div>
                {account.masked_number && (
                  <div style={{ opacity: 0.7 }}>•••• {account.masked_number}</div>
                )}
              </div>
            )}
          </div>

          {/* Transaction Type Icon */}
          <div className="flex-shrink-0">
            {isTransfer ? (
              <ArrowLeftRight className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
            ) : isIncome ? (
              <BanknoteArrowUp className="h-5 w-5" style={{ color: 'var(--theme-income)' }} />
            ) : (
              <BanknoteArrowDown className="h-5 w-5" style={{ color: 'var(--theme-expense)' }} />
            )}
          </div>

          {/* Date */}
          <div className="min-w-[100px] flex-shrink-0">
            {isEditingDate ? (
              <input
                type="date"
                value={editingField.value as string}
                onChange={(e) => handleDateChange(e.target.value)}
                onBlur={handleFieldBlur}
                autoFocus
                className="px-2 py-1 text-sm rounded-lg"
                style={{ 
                  backgroundColor: 'var(--theme-surface)',
                  borderColor: 'var(--theme-primary)',
                  color: 'var(--theme-text)'
                }}
              />
            ) : (
              <span 
                className="text-sm cursor-pointer transition-colors"
                style={{ color: 'var(--theme-text-secondary)' }}
                onClick={() => handleFieldClick(transaction, 'transaction_date')}
              >
                {formatDateFull(transaction.transaction_date)}
              </span>
            )}
          </div>

          {/* Merchant with Logo */}
          <div className="flex items-center gap-3 min-w-[200px] flex-shrink-0">
            <Tooltip content={transaction.original_description || transaction.merchant_name} label="Raw Vendor Name">
              <div className="flex-shrink-0">
                {hasLogo ? (
                  <img 
                    src={transaction.merchant_logo_url || undefined} 
                    alt={`${transaction.merchant_name} logo`}
                    className="h-9 w-9 rounded-xl object-cover"
                    onError={() => setLogoErrors(prev => new Set(prev).add(transaction.id))}
                  />
                ) : (
                  <div 
                    className="h-9 w-9 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)' }}
                  >
                    <Store className="h-4 w-4" style={{ color: 'var(--theme-text-secondary)' }} />
                  </div>
                )}
              </div>
            </Tooltip>
            {isEditingMerchant ? (
              <input
                type="text"
                value={editingField.value as string}
                onChange={(e) => handleFieldChange(e.target.value)}
                onBlur={handleFieldBlur}
                autoFocus
                className="px-2 py-1 text-sm font-medium rounded-lg w-[150px]"
                style={{ 
                  backgroundColor: 'var(--theme-surface)',
                  borderColor: 'var(--theme-primary)',
                  color: 'var(--theme-text)'
                }}
              />
            ) : (
              <span 
                className="font-semibold cursor-pointer transition-colors truncate"
                style={{ color: 'var(--theme-text)' }}
                onClick={() => handleFieldClick(transaction, 'merchant_name')}
              >
                {transaction.merchant_name}
              </span>
            )}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Category - hidden for transfers */}
          {!isTransfer && (
            <div className="flex-shrink-0">
              {isEditingCategory ? (
                <select
                  value={editingField.value as string}
                  onChange={(e) => handleFieldChange(e.target.value)}
                  onBlur={handleFieldBlur}
                  autoFocus
                  className="px-3 py-1.5 text-xs rounded-xl"
                  style={{ 
                    backgroundColor: 'var(--theme-surface)',
                    borderColor: 'var(--theme-primary)',
                    color: 'var(--theme-text)'
                  }}
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              ) : (
                <button
                  className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium cursor-pointer transition-all"
                  style={{ 
                    backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)',
                    color: 'var(--theme-text)'
                  }}
                  onClick={() => handleFieldClick(transaction, 'category')}
                >
                  {hasRecommendation && (
                    <div 
                      className="relative"
                      onMouseEnter={() => setHoveredRecommendation(transaction.id)}
                      onMouseLeave={() => setHoveredRecommendation(null)}
                    >
                      <Sparkles className="h-3.5 w-3.5" style={{ color: 'var(--theme-primary)' }} />
                      {isHoveredRecommendation && recommendation && (
                        <div 
                          className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs font-medium rounded-xl shadow-lg whitespace-nowrap max-w-xs"
                          style={{ 
                            backgroundColor: 'var(--theme-text)',
                            color: 'var(--theme-background)'
                          }}
                        >
                          <div className="font-semibold">AI suggested: {recommendation.recommended_category}</div>
                          {recommendation.reasoning && (
                            <div style={{ opacity: 0.7, marginTop: '4px' }} className="whitespace-normal">{recommendation.reasoning}</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <span>{transaction.category}</span>
                  <ChevronDown className="h-3 w-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                </button>
              )}
            </div>
          )}

          {/* Amount with Transfer Toggle */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => handleToggleTransfer(transaction)}
              className="p-2 rounded-xl transition-colors"
              style={{ 
                backgroundColor: isTransfer 
                  ? 'color-mix(in srgb, var(--theme-accent) 15%, transparent)' 
                  : 'transparent',
                color: isTransfer ? 'var(--theme-accent)' : 'var(--theme-text-secondary)'
              }}
              title={isTransfer ? 'Remove transfer label' : 'Mark as transfer'}
            >
              <ArrowLeftRight className="h-4 w-4" />
            </button>
            
            <div className="text-right min-w-[100px]">
              <span 
                className="font-bold"
                style={{ 
                  color: isTransfer 
                    ? 'var(--theme-accent)' 
                    : isIncome 
                      ? 'var(--theme-income)' 
                      : 'var(--theme-text)' 
                }}
              >
                {isTransfer ? '' : (isIncome ? '+' : '-')}{formatAmount(transaction.amount)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="overflow-x-hidden" style={{ backgroundColor: 'var(--theme-background)' }}>
      {/* Header */}
      <div className="px-4 pt-6 pb-4 md:px-8 md:pt-8 md:pb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          <div className="fade-in-up">
            <h1 
              className="text-2xl md:text-3xl font-bold tracking-tight"
              style={{ color: 'var(--theme-text)' }}
            >
              Transactions
            </h1>
            <p 
              className="text-sm mt-1"
              style={{ color: 'var(--theme-text-secondary)' }}
            >
              {totalCount.toLocaleString()} total transactions
            </p>
          </div>
          
          <div className="flex items-center gap-2 fade-in-up" style={{ animationDelay: '100ms' }}>
            {/* Search Button/Input */}
            <div className="relative">
              {isSearchOpen ? (
                <div 
                  className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all"
                  style={{ 
                    backgroundColor: 'var(--theme-surface)',
                    border: '1px solid var(--theme-primary)'
                  }}
                >
                  <Search className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--theme-text-secondary)' }} />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search vendor or category..."
                    className="bg-transparent outline-none text-sm w-40 sm:w-56"
                    style={{ color: 'var(--theme-text)' }}
                  />
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setDebouncedSearch('');
                      setIsSearchOpen(false);
                    }}
                    className="p-1 rounded-lg hover:bg-black/5 transition-colors"
                    style={{ color: 'var(--theme-text-secondary)' }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setIsSearchOpen(true)}
                  className="p-2.5 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                  style={{ 
                    backgroundColor: debouncedSearch 
                      ? 'color-mix(in srgb, var(--theme-primary) 15%, var(--theme-surface))'
                      : 'var(--theme-surface)',
                    border: debouncedSearch 
                      ? '1px solid var(--theme-primary)'
                      : '1px solid color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)',
                    color: debouncedSearch ? 'var(--theme-primary)' : 'var(--theme-text-secondary)'
                  }}
                  title="Search transactions"
                >
                  <Search className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setIsFilterOpen(true)}
              className="relative p-2.5 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              style={{ 
                backgroundColor: activeFilterCount > 0 
                  ? 'color-mix(in srgb, var(--theme-primary) 15%, var(--theme-surface))'
                  : 'var(--theme-surface)',
                border: activeFilterCount > 0 
                  ? '1px solid var(--theme-primary)'
                  : '1px solid color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)',
                color: activeFilterCount > 0 ? 'var(--theme-primary)' : 'var(--theme-text-secondary)'
              }}
              title="Filter transactions"
            >
              <Filter className="h-5 w-5" />
              {activeFilterCount > 0 && (
                <span 
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center"
                  style={{ 
                    backgroundColor: 'var(--theme-primary)', 
                    color: 'white' 
                  }}
                >
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Filter Modal */}
            <Modal 
              open={isFilterOpen} 
              onClose={() => setIsFilterOpen(false)}
              title="Filters"
            >
              {/* Clear All Button */}
              {activeFilterCount > 0 && (
                <div className="px-5 py-3 border-b" style={{ borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)' }}>
                  <button
                    onClick={() => {
                      setQuickFilterCategory(null);
                      setQuickFilterAccount(null);
                      setQuickFilterMonth(null);
                    }}
                    className="text-sm font-medium px-3 py-1.5 rounded-lg transition-colors"
                    style={{ 
                      color: 'var(--theme-expense)',
                      backgroundColor: 'color-mix(in srgb, var(--theme-expense) 10%, transparent)'
                    }}
                  >
                    Clear all filters
                  </button>
                </div>
              )}

              {/* Account Filter Section */}
              {accounts.length > 0 && (
                <div className="px-5 py-4 border-b" style={{ borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="h-4 w-4" style={{ color: 'var(--theme-text-secondary)' }} />
                    <span className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>Account</span>
                  </div>
                  <div className="space-y-1">
                    <button
                      onClick={() => handleAccountFilter(null)}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors"
                      style={{ 
                        backgroundColor: !quickFilterAccount 
                          ? 'color-mix(in srgb, var(--theme-primary) 15%, transparent)' 
                          : 'transparent',
                        color: !quickFilterAccount ? 'var(--theme-primary)' : 'var(--theme-text)'
                      }}
                    >
                      <span>All Accounts</span>
                      {!quickFilterAccount && <Check className="h-4 w-4" />}
                    </button>
                    {accounts.map((account) => (
                      <button
                        key={account.id}
                        onClick={() => handleAccountFilter(account.id)}
                        className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors"
                        style={{ 
                          backgroundColor: quickFilterAccount === account.id 
                            ? 'color-mix(in srgb, var(--theme-primary) 15%, transparent)' 
                            : 'transparent',
                          color: quickFilterAccount === account.id ? 'var(--theme-primary)' : 'var(--theme-text)'
                        }}
                      >
                        <span className="truncate mr-2">
                          {account.name}{account.masked_number ? ` •••• ${account.masked_number}` : ''}
                        </span>
                        {quickFilterAccount === account.id && <Check className="h-4 w-4 flex-shrink-0" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Category Filter Section */}
              <div className="px-5 py-4 border-b" style={{ borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 15%, transparent)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="h-4 w-4" style={{ color: 'var(--theme-text-secondary)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>Category</span>
                </div>
                <div className="space-y-1">
                  <button
                    onClick={() => handleCategoryQuickFilter(null)}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors"
                    style={{ 
                      backgroundColor: !quickFilterCategory 
                        ? 'color-mix(in srgb, var(--theme-primary) 15%, transparent)' 
                        : 'transparent',
                      color: !quickFilterCategory ? 'var(--theme-primary)' : 'var(--theme-text)'
                    }}
                  >
                    <span>All Categories</span>
                    {!quickFilterCategory && <Check className="h-4 w-4" />}
                  </button>
                  
                  {/* Expense Categories */}
                  <div className="pt-3 pb-1">
                    <span className="text-xs font-medium px-3 uppercase tracking-wider" style={{ color: 'var(--theme-expense)' }}>
                      Expenses
                    </span>
                  </div>
                  {EXPENSE_CATEGORIES.map((category) => (
                    <button
                      key={category}
                      onClick={() => handleCategoryQuickFilter(category)}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors"
                      style={{ 
                        backgroundColor: quickFilterCategory === category 
                          ? 'color-mix(in srgb, var(--theme-primary) 15%, transparent)' 
                          : 'transparent',
                        color: quickFilterCategory === category ? 'var(--theme-primary)' : 'var(--theme-text)'
                      }}
                    >
                      <span className="truncate mr-2">{category}</span>
                      {quickFilterCategory === category && <Check className="h-4 w-4 flex-shrink-0" />}
                    </button>
                  ))}
                  
                  {/* Income Categories */}
                  <div className="pt-3 pb-1">
                    <span className="text-xs font-medium px-3 uppercase tracking-wider" style={{ color: 'var(--theme-income)' }}>
                      Income
                    </span>
                  </div>
                  {INCOME_CATEGORIES.map((category) => (
                    <button
                      key={category}
                      onClick={() => handleCategoryQuickFilter(category)}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors"
                      style={{ 
                        backgroundColor: quickFilterCategory === category 
                          ? 'color-mix(in srgb, var(--theme-primary) 15%, transparent)' 
                          : 'transparent',
                        color: quickFilterCategory === category ? 'var(--theme-primary)' : 'var(--theme-text)'
                      }}
                    >
                      <span className="truncate mr-2">{category}</span>
                      {quickFilterCategory === category && <Check className="h-4 w-4 flex-shrink-0" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Month Filter Section */}
              {availableMonths.length > 0 && (
                <div className="px-5 py-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="h-4 w-4" style={{ color: 'var(--theme-text-secondary)' }} />
                    <span className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>Month</span>
                  </div>
                  <div className="space-y-1">
                    <button
                      onClick={() => setQuickFilterMonth(null)}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors"
                      style={{ 
                        backgroundColor: !quickFilterMonth 
                          ? 'color-mix(in srgb, var(--theme-primary) 15%, transparent)' 
                          : 'transparent',
                        color: !quickFilterMonth ? 'var(--theme-primary)' : 'var(--theme-text)'
                      }}
                    >
                      <span>All Time</span>
                      {!quickFilterMonth && <Check className="h-4 w-4" />}
                    </button>
                    {availableMonths.map((month) => (
                      <button
                        key={month}
                        onClick={() => handleMonthQuickFilter(month)}
                        className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors"
                        style={{ 
                          backgroundColor: quickFilterMonth === month 
                            ? 'color-mix(in srgb, var(--theme-primary) 15%, transparent)' 
                            : 'transparent',
                          color: quickFilterMonth === month ? 'var(--theme-primary)' : 'var(--theme-text)'
                        }}
                      >
                        <span>{month}</span>
                        {quickFilterMonth === month && <Check className="h-4 w-4" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </Modal>

            {/* Clear All Filters Button */}
            {hasActiveFilters && (
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2 rounded-xl h-11 hidden sm:flex"
                onClick={handleClearAllFilters}
                style={{ 
                  backgroundColor: 'color-mix(in srgb, var(--theme-expense) 10%, var(--theme-surface))',
                  borderColor: 'color-mix(in srgb, var(--theme-expense) 30%, transparent)',
                  color: 'var(--theme-expense)'
                }}
              >
                <XCircle className="h-4 w-4" />
                <span>Clear</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 pb-6 md:px-8 md:pb-8">
        {/* Statistics Cards */}
        <div className="fade-in-up" style={{ animationDelay: '150ms' }}>
          <TransactionStats
            analytics={analytics}
            loading={analyticsLoading}
            onCategoryClick={handleCategoryQuickFilter}
            onMonthClick={handleMonthQuickFilter}
            activeCategory={quickFilterCategory}
            activeMonth={quickFilterMonth}
          />
        </div>

        {/* Active Filters Summary (Mobile) */}
        {hasActiveFilters && (
          <div 
            className="sm:hidden mb-4 flex flex-wrap items-center gap-2 fade-in-up"
            style={{ animationDelay: '150ms' }}
          >
            {debouncedSearch && (
              <span 
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{ 
                  backgroundColor: 'color-mix(in srgb, var(--theme-primary) 15%, transparent)',
                  color: 'var(--theme-primary)'
                }}
              >
                <Search className="h-3 w-3" />
                "{debouncedSearch}"
                <button onClick={() => { setSearchQuery(''); setDebouncedSearch(''); }}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {quickFilterAccount && (
              <span 
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{ 
                  backgroundColor: 'color-mix(in srgb, var(--theme-primary) 15%, transparent)',
                  color: 'var(--theme-primary)'
                }}
              >
                <Building2 className="h-3 w-3" />
                {accounts.find(a => a.id === quickFilterAccount)?.name || 'Account'}
                <button onClick={() => setQuickFilterAccount(null)}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {quickFilterCategory && (
              <span 
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{ 
                  backgroundColor: 'color-mix(in srgb, var(--theme-primary) 15%, transparent)',
                  color: 'var(--theme-primary)'
                }}
              >
                <Tag className="h-3 w-3" />
                {quickFilterCategory}
                <button onClick={() => setQuickFilterCategory(null)}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {quickFilterMonth && (
              <span 
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{ 
                  backgroundColor: 'color-mix(in srgb, var(--theme-primary) 15%, transparent)',
                  color: 'var(--theme-primary)'
                }}
              >
                <Calendar className="h-3 w-3" />
                {quickFilterMonth}
                <button onClick={() => setQuickFilterMonth(null)}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            <button
              onClick={handleClearAllFilters}
              className="text-xs font-medium px-2 py-1.5 rounded-lg"
              style={{ 
                color: 'var(--theme-expense)',
                backgroundColor: 'color-mix(in srgb, var(--theme-expense) 10%, transparent)'
              }}
            >
              Clear all
            </button>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div 
            className="apple-card mb-4 !p-4 fade-in-up"
            style={{ 
              backgroundColor: 'color-mix(in srgb, var(--theme-expense) 10%, var(--theme-surface))',
              borderColor: 'color-mix(in srgb, var(--theme-expense) 30%, transparent)'
            }}
          >
            <p className="text-sm font-medium" style={{ color: 'var(--theme-expense)' }}>
              {error}
            </p>
          </div>
        )}

        {/* Quick Filter Tags */}
        {!loading && typeCounts.all > 0 && (
          <div 
            className="mb-4 flex flex-wrap items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 fade-in-up scrollbar-hide"
            style={{ animationDelay: '200ms' }}
          >
            {/* All badge */}
            <button
              onClick={() => {
                handleTypeQuickFilter(null);
                handleCategoryQuickFilter(null);
              }}
              className="px-4 py-2 rounded-xl text-sm font-semibold transition-all whitespace-nowrap"
              style={
                quickFilterType === null && quickFilterCategory === null
                  ? { backgroundColor: 'var(--theme-primary)', color: 'white' }
                  : { 
                      backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)', 
                      color: 'var(--theme-text-secondary)' 
                    }
              }
            >
              All <span className="ml-1 opacity-70">{typeCounts.all}</span>
            </button>

            {/* Uncategorized badge */}
            {typeCounts.uncategorized > 0 && (
              <button
                onClick={() => {
                  handleTypeQuickFilter(null);
                  handleCategoryQuickFilter('Uncategorized');
                }}
                className="px-4 py-2 rounded-xl text-sm font-semibold transition-all whitespace-nowrap"
                style={
                  quickFilterCategory === 'Uncategorized'
                    ? { backgroundColor: 'var(--theme-primary)', color: 'white' }
                    : { 
                        backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)', 
                        color: 'var(--theme-text-secondary)' 
                      }
                }
              >
                Uncategorized <span className="ml-1 opacity-70">{typeCounts.uncategorized}</span>
              </button>
            )}

            {/* Expenses badge */}
            <button
              onClick={() => handleTypeQuickFilter('expense')}
              className="px-4 py-2 rounded-xl text-sm font-semibold transition-all whitespace-nowrap"
              style={
                quickFilterType === 'expense'
                  ? { backgroundColor: 'var(--theme-expense)', color: 'white' }
                  : { 
                      backgroundColor: 'color-mix(in srgb, var(--theme-expense) 12%, transparent)', 
                      color: 'var(--theme-expense)' 
                    }
              }
            >
              Expenses <span className="ml-1 opacity-70">{typeCounts.expense}</span>
            </button>

            {/* Income badge */}
            <button
              onClick={() => handleTypeQuickFilter('income')}
              className="px-4 py-2 rounded-xl text-sm font-semibold transition-all whitespace-nowrap"
              style={
                quickFilterType === 'income'
                  ? { backgroundColor: 'var(--theme-income)', color: 'white' }
                  : { 
                      backgroundColor: 'color-mix(in srgb, var(--theme-income) 12%, transparent)', 
                      color: 'var(--theme-income)' 
                    }
              }
            >
              Income <span className="ml-1 opacity-70">{typeCounts.income}</span>
            </button>

            {/* Transfers badge */}
            <button
              onClick={() => handleTypeQuickFilter('transfer')}
              className="px-4 py-2 rounded-xl text-sm font-semibold transition-all whitespace-nowrap"
              style={
                quickFilterType === 'transfer'
                  ? { backgroundColor: 'var(--theme-accent)', color: 'white' }
                  : { 
                      backgroundColor: 'color-mix(in srgb, var(--theme-accent) 12%, transparent)', 
                      color: 'var(--theme-accent)' 
                    }
              }
            >
              Transfers <span className="ml-1 opacity-70">{typeCounts.transfer}</span>
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div 
              className="w-10 h-10 border-3 rounded-full animate-spin mb-4"
              style={{ 
                borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)',
                borderTopColor: 'var(--theme-primary)'
              }}
            />
            <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
              Loading transactions...
            </p>
          </div>
        ) : (
          <>
            {/* Transaction List */}
            {(transactions.length > 0 || hasActiveFilters) && (
              <div
                className="apple-card !p-0 overflow-hidden fade-in-up"
                style={{ animationDelay: '250ms' }}
              >
                {transactions.length === 0 ? (
                  <div className="text-center py-16 px-4">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                      style={{ backgroundColor: 'color-mix(in srgb, var(--theme-primary) 12%, transparent)' }}
                    >
                      <Search className="h-8 w-8" style={{ color: 'var(--theme-primary)' }} />
                    </div>
                    <p
                      className="font-semibold mb-1"
                      style={{ color: 'var(--theme-text)' }}
                    >
                      {debouncedSearch
                        ? `No results for "${debouncedSearch}"`
                        : 'No matching transactions'}
                    </p>
                    <p
                      className="text-sm"
                      style={{ color: 'var(--theme-text-secondary)' }}
                    >
                      {debouncedSearch
                        ? 'Try a different search term or adjust your filters'
                        : 'Try adjusting your filters'}
                    </p>
                  </div>
                ) : (
                  <div>
                    {transactions.map((transaction, index) => renderTransactionItem(transaction, index))}
                  </div>
                )}
              </div>
            )}
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div 
                className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 fade-in-up"
                style={{ animationDelay: '300ms' }}
              >
                <p 
                  className="text-sm order-2 sm:order-1"
                  style={{ color: 'var(--theme-text-secondary)' }}
                >
                  <span className="hidden sm:inline">Showing </span>
                  {transactions.length > 0 ? ((currentPage - 1) * pageSize + 1) : 0}-{Math.min(currentPage * pageSize, totalCount)} of {totalCount}
                </p>
                <div className="flex items-center gap-2 order-1 sm:order-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCurrentPage(prev => Math.max(1, prev - 1));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    disabled={currentPage === 1}
                    className="gap-1 rounded-xl"
                    style={{ 
                      backgroundColor: 'var(--theme-surface)',
                      borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)',
                      color: 'var(--theme-text)'
                    }}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline">Previous</span>
                  </Button>
                  
                  {/* Mobile: Simple page indicator */}
                  <span 
                    className="text-sm px-3 md:hidden font-medium"
                    style={{ color: 'var(--theme-text-secondary)' }}
                  >
                    {currentPage} / {totalPages}
                  </span>
                  
                  {/* Desktop: Page numbers */}
                  <div className="hidden md:flex items-center gap-1">
                    {(() => {
                      const pages: (number | 'ellipsis')[] = [];
                      const maxVisible = 7;
                      
                      if (totalPages <= maxVisible) {
                        for (let i = 1; i <= totalPages; i++) pages.push(i);
                      } else {
                        pages.push(1);
                        if (currentPage > 3) pages.push('ellipsis');
                        
                        const start = Math.max(2, currentPage - 1);
                        const end = Math.min(totalPages - 1, currentPage + 1);
                        for (let i = start; i <= end; i++) pages.push(i);
                        
                        if (currentPage < totalPages - 2) pages.push('ellipsis');
                        pages.push(totalPages);
                      }
                      
                      return pages.map((page, index) => {
                        if (page === 'ellipsis') {
                          return (
                            <span 
                              key={`ellipsis-${index}`} 
                              className="px-2"
                              style={{ color: 'var(--theme-text-secondary)' }}
                            >
                              ...
                            </span>
                          );
                        }
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              setCurrentPage(page);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="min-w-[36px] rounded-xl"
                            style={currentPage === page ? {
                              backgroundColor: 'var(--theme-primary)',
                              color: 'white'
                            } : {
                              backgroundColor: 'var(--theme-surface)',
                              borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)',
                              color: 'var(--theme-text)'
                            }}
                          >
                            {page}
                          </Button>
                        );
                      });
                    })()}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCurrentPage(prev => Math.min(totalPages, prev + 1));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    disabled={currentPage === totalPages}
                    className="gap-1 rounded-xl"
                    style={{ 
                      backgroundColor: 'var(--theme-surface)',
                      borderColor: 'color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)',
                      color: 'var(--theme-text)'
                    }}
                  >
                    <span className="hidden sm:inline">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
